// Plan 08-02 — Google Identity Services SDK loader and One Tap helpers.
//
// SDK source: https://accounts.google.com/gsi/client
// One Tap shape (Pitfall 2):
//   google.accounts.id.initialize({ client_id, callback, ux_mode: 'popup',
//                                   auto_select })
//   google.accounts.id.prompt()
//
// The callback receives { credential: <id_token>, select_by, clientId }.

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (response: {
              credential: string
              select_by?: string
              clientId?: string
            }) => void
            ux_mode?: 'popup' | 'redirect'
            auto_select?: boolean
            cancel_on_tap_outside?: boolean
            // Plan 09-11 / CR-01: SHA-256 hex digest of the raw nonce that
            // travels in the exchange POST body. Google embeds THIS value
            // in the JWT's nonce claim; backend (jwks.ts) re-hashes the
            // body nonce and compares.
            nonce?: string
          }) => void
          prompt: (
            momentListener?: (notification: {
              isNotDisplayed?: () => boolean
              isSkippedMoment?: () => boolean
              isDismissedMoment?: () => boolean
              getNotDisplayedReason?: () => string
              getSkippedReason?: () => string
              getDismissedReason?: () => string
            }) => void,
          ) => void
          disableAutoSelect: () => void
        }
        oauth2: {
          initCodeClient: (config: {
            client_id: string
            scope: string
            ux_mode?: 'popup' | 'redirect'
            redirect_uri?: string
            callback: (response: {
              code?: string
              scope?: string
              state?: string
              error?: string
              error_description?: string
            }) => void
            error_callback?: (error: {
              type?: string
              message?: string
            }) => void
            select_account?: boolean
            state?: string
          }) => {
            requestCode: () => void
          }
        }
      }
    }
  }
}

let sdkPromise: Promise<void> | null = null

export function loadGoogleSDK(): Promise<void> {
  if (sdkPromise) return sdkPromise
  sdkPromise = new Promise<void>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('loadGoogleSDK requires a DOM'))
      return
    }
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-sdk="google-gis"]',
    )
    if (existing) {
      if (window.google?.accounts?.id) {
        resolve()
      } else {
        existing.addEventListener('load', () => resolve(), { once: true })
        existing.addEventListener(
          'error',
          () => reject(new Error('Google SDK failed to load')),
          { once: true },
        )
      }
      return
    }
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.dataset.sdk = 'google-gis'
    script.addEventListener('load', () => resolve(), { once: true })
    script.addEventListener(
      'error',
      () => reject(new Error('Google SDK failed to load')),
      { once: true },
    )
    document.head.appendChild(script)
  })
  return sdkPromise
}

function getClientId(): string {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
  if (!clientId || clientId.startsWith('<')) {
    throw new Error('VITE_GOOGLE_CLIENT_ID not configured')
  }
  return clientId
}

/**
 * Plan 09-11 / CR-01: Google's GIS initialize() requires the `nonce` field
 * to be the SHA-256 hex digest of the raw nonce that travels in the
 * exchange POST body. The JWT therefore embeds the SHA-256 hex digest in
 * its nonce claim; the backend (apps/backend/src/auth/jwks.ts) re-hashes
 * the body nonce and compares.
 *
 * https://developers.google.com/identity/gsi/web/reference/js-reference#nonce
 */
async function sha256HexForGoogle(rawNonce: string): Promise<string> {
  const data = new TextEncoder().encode(rawNonce)
  const buf = await crypto.subtle.digest('SHA-256', data)
  const bytes = new Uint8Array(buf)
  let out = ''
  for (let i = 0; i < bytes.length; i += 1) {
    const v = bytes[i] ?? 0
    out += v.toString(16).padStart(2, '0')
  }
  return out
}

/**
 * Initial-sign-in via the OAuth 2.0 authorization-code popup flow
 * (`google.accounts.oauth2.initCodeClient`). Opens Google's full account
 * picker / sign-in form in a popup window — works whether or not the user
 * already has a Google session in the browser, unlike One Tap / FedCM.
 *
 * Caller receives the OAuth `code` via `onCode` and is responsible for
 * POSTing it to `/api/auth/exchange` with `{ idp_kind:'google', code,
 * redirect_uri:'postmessage' }`. The backend swaps the code for an id_token
 * at Google's token endpoint using the configured `GOOGLE_CLIENT_SECRET`.
 *
 * Note: the One Tap / id_token flow (used by silent refresh below) is a
 * separate API surface. This function does NOT compete with it.
 */
export async function signInWithGoogleCode(opts: {
  onCode: (code: string) => void
  onError?: (err: Error) => void
}): Promise<void> {
  await loadGoogleSDK()
  if (!window.google?.accounts?.oauth2) {
    throw new Error(
      'Google SDK loaded but window.google.accounts.oauth2 is undefined',
    )
  }
  const client = window.google.accounts.oauth2.initCodeClient({
    client_id: getClientId(),
    scope: 'openid email profile',
    ux_mode: 'popup',
    callback: (response) => {
      if (response.error) {
        opts.onError?.(
          new Error(
            response.error_description ??
              `Google auth error: ${response.error}`,
          ),
        )
        return
      }
      if (typeof response.code === 'string' && response.code.length > 0) {
        opts.onCode(response.code)
        return
      }
      opts.onError?.(new Error('Google returned no auth code'))
    },
    error_callback: (err) => {
      opts.onError?.(new Error(err.message ?? 'Google auth aborted'))
    },
  })
  client.requestCode()
}

/**
 * Silent re-auth via Google One Tap with `auto_select: true`. Resolves with
 * the IdP-issued id_token when GIS dispatches its callback, or rejects
 * after the One Tap moment is dismissed/skipped without firing.
 *
 * Plan 09-11 / CR-01: caller-provided nonce is SHA-256-hashed for GIS so
 * the JWT carries the hashed value; backend re-hashes the body nonce and
 * compares.
 */
export async function silentReauthGoogle(opts: {
  nonce: string
}): Promise<string> {
  await loadGoogleSDK()
  if (!window.google?.accounts?.id) {
    throw new Error(
      'Google SDK loaded but window.google.accounts.id is undefined',
    )
  }
  const hashedNonce = await sha256HexForGoogle(opts.nonce)
  return new Promise<string>((resolve, reject) => {
    let settled = false
    const settleResolve = (token: string): void => {
      if (settled) return
      settled = true
      resolve(token)
    }
    const settleReject = (err: Error): void => {
      if (settled) return
      settled = true
      reject(err)
    }
    window.google!.accounts.id.initialize({
      client_id: getClientId(),
      callback: ({ credential }) => settleResolve(credential),
      ux_mode: 'popup',
      auto_select: true,
      nonce: hashedNonce, // CR-01: SHA-256 hash of raw body nonce
    })
    window.google!.accounts.id.prompt((notification) => {
      if (
        notification.isNotDisplayed?.() ||
        notification.isSkippedMoment?.() ||
        notification.isDismissedMoment?.()
      ) {
        settleReject(new Error('Google silent reauth not granted'))
      }
    })
  })
}
